// «Cobros» del portal del vendedor: mis créditos a la izquierda, con búsqueda en la cadena para quien
// puede cobrar en tienda, y el cupón a cobrar a la derecha.
import { useMemo, useState } from 'react';
import { ScreenFrame, ScreenFrameNotice } from '@/shared/patterns/ScreenFrame';
import { mxDay } from '@/lib/dateHelpers';
import { useAuth } from '@/hooks/useAuth';
import type { Permission } from '@/api/types/permissions';
import type { SellerPaymentResult } from '@/api/services/loan-credit';
import { useSaleDevice } from './mysales/api/useSaleDevice';
import {
    paymentsOfSellerLoad,
    useLastPayments,
    useMyCredits,
    useSellerCredit,
    useWalkIn,
} from './collections/api/useCollections';
import {
    CollectionsListPanel,
    type ListEntry,
} from './collections/components/CollectionsListPanel';
import { CollectionDetailPanel } from './collections/components/CollectionDetailPanel';
import {
    creditFactsOf,
    factsOfRow,
    filterCounts,
    visibleRows,
    walkInKindOf,
    type CollectionCredit,
    type CollectionFilter,
} from './collections/lib/collectionsModel';
import {
    COLLECTIONS_TRUNCATED_NOTE,
    CREDIT_FORBIDDEN_NOTE,
    SELLER_NOT_REGISTERED_NOTE,
    WALK_IN_ERROR_NOTE,
    WALK_IN_FORBIDDEN_NOTE,
} from './collections/lib/collectionsFormat';

export function SalesCollections() {
    const { hasPermission } = useAuth();
    // El walk-in y el cobro en tienda van con el mismo permiso de escritura.
    const canCollect = hasPermission('loan.credit.collection.write' as Permission);
    const today = mxDay(new Date().toISOString());
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState<CollectionFilter>('mine');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    // El comprobante y su fila: siguen en pantalla aunque el crédito salga de la lista al liquidarse.
    const [receipt, setReceipt] = useState<{
        row: CollectionCredit;
        result: SellerPaymentResult;
    } | null>(null);

    const mine = useMyCredits();
    const mineIds = useMemo(() => new Set(mine.rows.map((row) => row.creditId)), [mine.rows]);
    const inMine = useMemo(
        () => visibleRows(mine.rows, filter, query, today),
        [mine.rows, filter, query, today],
    );

    // Fuera de la cartera solo se busca si en la mía no hay nada y el texto es un identificador.
    const term = query.trim();
    const walkInKind =
        canCollect && term && inMine.length === 0 && !mine.isLoading ? walkInKindOf(term) : null;
    const walkIn = useWalkIn(walkInKind, term, mineIds);
    const walkInRows = walkIn.result?.state === 'ready' ? walkIn.result.rows : [];
    const isWalkIn = walkInRows.length > 0;
    const rows = isWalkIn ? walkInRows : inMine;

    const entries: ListEntry[] = rows.map((row) => ({ row, facts: factsOfRow(row, today) }));
    const counts = useMemo(() => filterCounts(mine.rows, today), [mine.rows, today]);

    // La fila abierta se deriva de lo visible: sin selección válida, la primera, como el mock.
    const opened = receipt
        ? (entries.find((entry) => entry.row.creditId === receipt.row.creditId) ?? {
              row: receipt.row,
          })
        : (entries.find((entry) => entry.row.creditId === selectedId) ?? entries.at(0) ?? null);
    const openedId = opened?.row.creditId ?? null;
    const isMineOpened = opened?.row.isMine ?? false;
    // Mis créditos se leen al abrirlos (crédito, plan y cobros); el walk-in ya trae su detalle.
    const seller = useSellerCredit(isMineOpened ? openedId : null);
    const openedRow = opened
        ? { ...opened.row, read: isMineOpened ? seller.load.read : opened.row.read }
        : null;
    const openedFacts =
        openedRow?.read.state === 'ready' ? creditFactsOf(openedRow.read.snapshot, today) : null;
    const walkInPayments = useLastPayments(
        !isMineOpened && openedRow?.read.state === 'walk-in' ? openedId : null,
    );
    const payments = isMineOpened ? paymentsOfSellerLoad(seller.load) : walkInPayments;
    const device = useSaleDevice(openedRow?.saleId ?? null);
    // Reintentar relee la cartera y también el detalle abierto.
    const retry = () => {
        mine.refetch();
        seller.refetch();
    };

    return (
        <ScreenFrame>
            {mine.forbidden ? (
                <ScreenFrameNotice tone="warning" role="status">
                    {CREDIT_FORBIDDEN_NOTE}
                </ScreenFrameNotice>
            ) : null}
            {mine.truncated ? (
                <ScreenFrameNotice tone="warning" role="status">
                    {COLLECTIONS_TRUNCATED_NOTE}
                </ScreenFrameNotice>
            ) : null}
            <div className="grid min-h-0 flex-1 grid-cols-[460px_minmax(0,1fr)] gap-3 overflow-hidden bg-background px-3.5 pb-3.5 pt-3">
                <div className="flex min-h-0 flex-col">
                    <CollectionsListPanel
                        query={query}
                        onQueryChange={setQuery}
                        filter={filter}
                        onFilterChange={setFilter}
                        counts={counts}
                        entries={entries}
                        selectedId={openedId}
                        onSelect={(creditId) => {
                            setReceipt(null);
                            setSelectedId(creditId);
                        }}
                        isWalkIn={isWalkIn}
                        canWalkIn={canCollect}
                        walkInNote={walkInNoteOf(walkIn.result?.state)}
                        isLoading={mine.isLoading || walkIn.isLoading}
                        error={mine.error}
                        blockedNote={mine.notRegistered ? SELLER_NOT_REGISTERED_NOTE : null}
                        onRetry={retry}
                    />
                </div>
                <section
                    aria-label="Detalle del crédito"
                    className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card"
                >
                    <CollectionDetailPanel
                        row={openedRow}
                        facts={openedFacts}
                        payments={payments}
                        device={device.state}
                        canCollect={canCollect}
                        receipt={receipt?.result ?? null}
                        onCharged={(result) => {
                            if (opened) setReceipt({ row: opened.row, result });
                        }}
                        onReceiptDone={() => {
                            setReceipt(null);
                        }}
                        onRetry={retry}
                    />
                </section>
            </div>
        </ScreenFrame>
    );
}

function walkInNoteOf(state: 'ready' | 'forbidden' | 'error' | undefined): string | null {
    if (state === 'forbidden') return WALK_IN_FORBIDDEN_NOTE;
    if (state === 'error') return WALK_IN_ERROR_NOTE;
    return null;
}
